import { Tooltip } from 'antd';
import { useSelector } from 'react-redux';

import { Pane, Icon } from 'components/common';
import { colours } from 'utils/theme';
import OrgFilterPanel from './OrgFilterPanel';
import OrganisationPurchasingFilters from './OrganisationPurchasingFilters';
import styled from 'styled-components';

import {
  getFilters,
  getOrganisationClinicalTrialFilters,
  getOrganisationResearchersLoading,
  getOrganisationView,
  getOrganisationWorkItemIsLoading,
  getPurchasingFilters,
  getOrganisationLoadingClinicalTrialData,
  getPurchasingLoading,
} from 'selectors/organisation';
import { orgPageExcludedFilters, tokenise } from 'utils/filters';
import { GetSearchKeysByView } from 'components/Search/Filters/keys';
import { searchOrigin } from 'components/Search/Results/constants';
import FitlersCount from 'components/Search/Filters/FitlersCount';

import {
  ORGANISATIONCLINICALTRIALSVIEW,
  ORGANISATIONPEOPLEVIEW,
  ORGANISATIONPURCHASINGVIEW,
  ORGANISATIONWORKITEMVIEW,
} from '../helpers';
import { OrganisationClinicalTrialFilters } from './OrganisationClinicalTrialFilters';

export const Content = styled.div`
  margin-top: 10px;
`;

export const SearchFiltersTitleContainer = styled.div`
  display: flex;
`;

const Help = () => (
  <Tooltip
    title='Search filters can help you easily refine your search to better target the results you are after.'
    placement='right'
  >
    <div style={{ cursor: 'pointer' }}>
      <Icon icon='help' color={colours.teal} size='xs' />
    </div>
  </Tooltip>
);

const OrganisationProfileFilters = ({
  organisation,
  onFilterUpdate,
  onworkItemFilterUpdate,
  handleCancel,
  onClinicalTrialFilterUpdate,
}) => {
  const view = useSelector(getOrganisationView);
  const loadingResearchers = useSelector(
    getOrganisationResearchersLoading(organisation.id)
  );
  const loadingWorkItems = useSelector(
    getOrganisationWorkItemIsLoading(organisation.id)
  );
  const loadingPurchasing = useSelector(getPurchasingLoading);
  const sharedFilters = useSelector(getFilters(organisation.id));
  const purchasingFilters = useSelector(getPurchasingFilters);
  const clinicalTrialsFilters = useSelector(
    getOrganisationClinicalTrialFilters(organisation.id)
  );

  const searchKeys = GetSearchKeysByView(searchOrigin.organisationPageSearch);

  const loadingClinicalTrials = useSelector(
    getOrganisationLoadingClinicalTrialData(organisation.id)
  );

  let filters = null;
  let tokens = [];

  if (view === ORGANISATIONPEOPLEVIEW || view === ORGANISATIONWORKITEMVIEW) {
    const isPeople = view === ORGANISATIONPEOPLEVIEW;
    filters = (
      <OrgFilterPanel
        organisationId={organisation.id}
        onFilterUpdate={isPeople ? onFilterUpdate : onworkItemFilterUpdate}
        loading={isPeople ? loadingResearchers : loadingWorkItems}
        handleCancel={handleCancel}
        searchLocation={
          isPeople
            ? 'OrganisationResearchersSearch'
            : 'OrganisationWorkItemsSearch'
        }
      />
    );
    // `date` belongs to the search area's date dropdown, not the filter panel,
    // and RESET_ALL_FILTERS seeds it to 'all' — counting it would show a
    // phantom filter on a fresh page.
    tokens = tokenise(sharedFilters ?? {}, searchKeys, [
      ...orgPageExcludedFilters,
      'date',
    ]);
  } else if (view === ORGANISATIONPURCHASINGVIEW) {
    filters = (
      <OrganisationPurchasingFilters
        organisation={organisation}
        loading={loadingPurchasing}
        handleCancel={handleCancel}
      />
    );
    tokens = tokenise(
      purchasingFilters || {},
      searchKeys,
      orgPageExcludedFilters
    );
  } else if (view === ORGANISATIONCLINICALTRIALSVIEW) {
    tokens = tokenise(
      clinicalTrialsFilters || {},
      searchKeys,
      orgPageExcludedFilters
    );
    filters = (
      <OrganisationClinicalTrialFilters
        organisationId={organisation.id}
        onFilterUpdate={onClinicalTrialFilterUpdate}
        loading={loadingClinicalTrials}
      />
    );
  }

  if (!filters) return null;

  return (
    organisation && (
      <Pane
        title={
          <SearchFiltersTitleContainer>
            {view === ORGANISATIONPEOPLEVIEW ||
            view === ORGANISATIONWORKITEMVIEW ||
            view === ORGANISATIONCLINICALTRIALSVIEW
              ? 'Organisation Filters'
              : 'Search Filters'}
            <FitlersCount count={tokens?.length} diameter={25} />
          </SearchFiltersTitleContainer>
        }
        headerRight={<Help />}
        style={{ paddingBottom: 5 }}
      >
        <Content>{filters}</Content>
      </Pane>
    )
  );
};

export default OrganisationProfileFilters;
